// Tiny fetch wrapper for the @realchem/api service.
//
// All calls to the API go through this module so the bearer token
// lookup, JSON encoding, and error decoding are centralised. The
// frontend never reads `localStorage` for the token directly — only
// `client.ts` does, via the `tokenStore` import.

import { tokenStore } from "./token";

/** API base URL — reads `VITE_API_URL` at build time, falls back to
 * the dev default `http://localhost:8787`. Configure for production
 * via `.env` at the web app root. */
const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ??
  "http://localhost:8787";

export interface User {
  id: string;
  email: string;
  /** Activation code that was consumed to create this account.
   *  Null only for legacy accounts created before licensing
   *  shipped. The front-end uses this to rehydrate the
   *  license-gate token after a fresh signin so the user can
   *  reach the app from a new browser without re-entering a
   *  (now-redeemed) code. */
  license_code?: string | null;
}

export interface SavedExperiment {
  id: string;
  user_id: string;
  name: string;
  payload: unknown;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface SavedCourse {
  id: string;
  user_id: string;
  mode: string;
  title: string;
  /** The full Course document (steps, narration, experiment specs). */
  payload: unknown;
  created_at: string;
  updated_at: string;
}

export interface SavedFormula {
  id: string;
  user_id: string;
  source: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

/** Compact, pre-computed summary of one Atomic Mode molecular-dynamics run,
 * sent to `/ai/atomic-insight`. The client owns the measurement stream and
 * reduces it to these observables; the server only narrates them. */
export interface AtomicRunSummary {
  materials: string[];
  elements: string[];
  /** Interaction model: a recipe id, "reaxff_reactive", "hybrid_carbon", or
   * "UFF Lennard-Jones" for the universal fallback. */
  interactionModel: string;
  atomCount: number;
  /** Energy unit of peStart/peEnd/deltaPe: "eV" (metal) or "kcal/mol" (real). */
  energyUnit: string;
  temperatureK: number | null;
  steps: number;
  peStart: number | null;
  peEnd: number | null;
  deltaPe: number | null;
  msdEnd: number | null;
  /** Self-diffusion coefficient in Å²/ps. */
  diffusion: number | null;
  reactionDetected: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`api error ${status}: ${code}`);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (init.auth) {
    const token = tokenStore.get();
    if (!token) {
      throw new ApiError(401, "no_token");
    }
    headers.set("Authorization", `Bearer ${token}`);
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
  });
  if (!res.ok) {
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === "string") code = body.error;
    } catch {
      // body wasn't JSON; keep the http_<status> default.
    }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface AdminLicenseRow {
  code: string;
  redeemed: boolean;
  redeemed_at: string | null;
  created_by: string;
  created_at: string;
}

export const api = {
  signup: (email: string, password: string, licenseCode: string) =>
    request<{ token: string; user: User }>("/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password, license_code: licenseCode }),
    }),

  /** Public: validate (but do NOT consume) a license activation code.
   * The license gate calls this before letting the user past the
   * splash; signup is what actually marks the code redeemed. */
  checkLicense: (code: string) =>
    request<{ ok: true; code: string }>("/licenses/check", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  /** Admin: mint one or more license codes. Returns the new codes
   * verbatim so the admin can copy them out. The `x-admin-password`
   * header carries the shared secret. */
  adminMintLicenses: (
    adminPassword: string,
    label: string | undefined,
    count: number,
  ) =>
    request<{ codes: string[] }>("/admin/licenses", {
      method: "POST",
      headers: { "x-admin-password": adminPassword },
      body: JSON.stringify({ label, count }),
    }),

  adminListLicenses: (adminPassword: string) =>
    request<{ licenses: AdminLicenseRow[] }>("/admin/licenses", {
      method: "GET",
      headers: { "x-admin-password": adminPassword },
    }),

  signin: (email: string, password: string) =>
    request<{ token: string; user: User }>("/auth/signin", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<{ user: User }>("/me", { auth: true }),

  listExperiments: () =>
    request<{ experiments: SavedExperiment[] }>("/experiments", { auth: true }),

  listPublicExperiments: () =>
    request<{ experiments: SavedExperiment[] }>("/experiments/public"),

  saveExperiment: (name: string, payload: unknown, isPublic: boolean) =>
    request<{ experiment: SavedExperiment }>("/experiments", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ name, payload, public: isPublic }),
    }),

  listFormulas: () =>
    request<{ formulas: SavedFormula[] }>("/formulas", { auth: true }),

  listPublicFormulas: () =>
    request<{ formulas: SavedFormula[] }>("/formulas/public"),

  saveFormula: (source: string, isPublic: boolean) =>
    request<{ formula: SavedFormula }>("/formulas", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ source, public: isPublic }),
    }),

  textToExperiment: (prompt: string) =>
    request<{ json: string }>("/ai/text-to-experiment", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ prompt }),
    }),

  textToFormula: (prompt: string) =>
    request<{ source: string }>("/ai/text-to-formula", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ prompt }),
    }),

  /** Generate a full course from a prompt + difficulty mode. Returns
   * the course as a JSON string (the same `Course` schema the bundled
   * courses use); the caller validates + adds it to the registry. */
  textToCourse: (
    prompt: string,
    mode: "starter" | "explorer" | "inventor" | "genius",
  ) =>
    request<{ json: string }>("/ai/text-to-course", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ prompt, mode }),
    }),

  /** A signed-in user's saved (Text-to-Course) courses. Each row's
   * `payload` is the full Course document. */
  listCourses: () =>
    request<{ courses: SavedCourse[] }>("/courses", { auth: true }),

  /** Persist a generated course to the user's library. */
  saveCourse: (
    mode: "starter" | "explorer" | "inventor" | "genius",
    title: string,
    payload: unknown,
  ) =>
    request<{ course: SavedCourse }>("/courses", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ mode, title, payload }),
    }),

  mascotChat: (
    messages: Array<{ role: "user" | "assistant"; content: string }>,
  ) =>
    request<{ reply: string }>("/ai/chat", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ messages }),
    }),

  /** Turn a finished Atomic Mode run's measured observables into a concise
   * plain-text technical explanation for the live log. The caller passes an
   * already-summarised payload (materials, interaction model, energies, MSD,
   * diffusion, reaction flag); the server does a single cheap summarisation. */
  atomicInsight: (summary: AtomicRunSummary) =>
    request<{ insight: string }>("/ai/atomic-insight", {
      method: "POST",
      auth: true,
      body: JSON.stringify(summary),
    }),

  /** Every puzzle the authed user has solved -- consumed by the
   * puzzle runner on sign-in to seed its in-memory `completedIds`
   * set so the modal's ✓ chips show up immediately. */
  listPuzzleProgress: () =>
    request<{
      completed: Array<{ puzzle_id: string; completed_at: string }>;
    }>("/puzzles/completed", { auth: true }),

  /** Record a solve. Server is idempotent: a re-solve of an
   * already-completed puzzle is a silent no-op rather than an
   * error, so the runner can fire-and-forget from inside its
   * 250 ms goal-predicate loop. */
  savePuzzleProgress: (puzzleId: string) =>
    request<{
      completed: { puzzle_id: string; completed_at: string };
    }>("/puzzles/completed", {
      method: "POST",
      auth: true,
      body: JSON.stringify({ puzzle_id: puzzleId }),
    }),
};

export type MascotChatTurn = {
  role: "user" | "assistant";
  content: string;
};
